import { useEffect, useState, lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import { useStore, useIsAdmin, useIsYard, useIsPacker, useIsPM, useIsDelivery } from './stores/store';
import { defaultPath } from './lib/navItems';
import { LoginView } from './views/LoginView';
import { ToastHost } from './components/ToastHost';
import { ErrorBanner } from './components/ErrorBanner';

// Keep LoginView eager (first paint); everything else is route-split.
const AppShell = lazy(() => import('./views/AppShell').then((m) => ({ default: m.AppShell })));
const ManifestView = lazy(() => import('./views/ManifestView').then((m) => ({ default: m.ManifestView })));
const ScanPanel = lazy(() => import('./views/ScanPanel').then((m) => ({ default: m.ScanPanel })));
const InventoryPanel = lazy(() => import('./views/InventoryPanel').then((m) => ({ default: m.InventoryPanel })));
const ConfigPanel = lazy(() => import('./views/ConfigPanel').then((m) => ({ default: m.ConfigPanel })));
const PalletsPanel = lazy(() => import('./views/PalletsPanel').then((m) => ({ default: m.PalletsPanel })));
const PackingListsPanel = lazy(() => import('./views/PackingListsPanel').then((m) => ({ default: m.PackingListsPanel })));
const PackingRequestsPanel = lazy(() => import('./views/PackingRequestsPanel').then((m) => ({ default: m.PackingRequestsPanel })));
const ProjectsPanel = lazy(() => import('./views/ProjectsPanel').then((m) => ({ default: m.ProjectsPanel })));
const ActivityPanel = lazy(() => import('./views/ActivityPanel').then((m) => ({ default: m.ActivityPanel })));

const fullScreenLoading = (
	<Box
		sx={{
			position: 'fixed',
			inset: 0,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			bgcolor: 'background.default',
			color: 'text.secondary',
			fontSize: 14,
		}}
	>
		Loading…
	</Box>
);

export function App() {
	const user = useStore((s) => s.user);
	const loadingUser = useStore((s) => s.loadingUser);
	const bootstrap = useStore((s) => s.bootstrap);
	const [booted, setBooted] = useState(false);

	const isAdmin = useIsAdmin();
	const isYard = useIsYard();
	const isPacker = useIsPacker();
	const isPM = useIsPM();
	const isDelivery = useIsDelivery();
	const roles = { isAdmin, isYard, isPacker, isPM, isDelivery };

	useEffect(() => {
		bootstrap().finally(() => setBooted(true));
	}, [bootstrap]);

	const home = defaultPath(roles);
	const guard = (ok: boolean, el: ReactNode): ReactNode => (ok ? el : <Navigate to={home} replace />);

	let content: ReactNode;
	if (!booted || loadingUser) {
		content = fullScreenLoading;
	} else if (!user) {
		content = <LoginView />;
	} else {
		content = (
			<Suspense fallback={fullScreenLoading}>
				<Routes>
					<Route path="/manifest/:id" element={<ManifestView />} />
					<Route path="/" element={<AppShell />}>
						<Route index element={<Navigate to={home} replace />} />
						<Route path="scan" element={guard(isYard || isAdmin, <ScanPanel />)} />
						<Route path="inventory" element={<InventoryPanel />} />
						<Route path="pallets" element={guard(isYard || isDelivery || isAdmin, <PalletsPanel />)} />
						<Route path="pallets/:id" element={guard(isYard || isDelivery || isAdmin, <PalletsPanel />)} />
						<Route path="packing" element={guard(isPacker || isDelivery || isAdmin, <PackingListsPanel />)} />
						<Route path="packing/:id" element={guard(isPacker || isDelivery || isAdmin, <PackingListsPanel />)} />
						<Route path="requests" element={guard(isPM || isPacker || isAdmin, <PackingRequestsPanel />)} />
						<Route path="requests/:id" element={guard(isPM || isPacker || isAdmin, <PackingRequestsPanel />)} />
						<Route path="projects" element={<ProjectsPanel />} />
						<Route path="projects/:name" element={<ProjectsPanel />} />
						<Route path="activity" element={guard(isAdmin, <ActivityPanel />)} />
						<Route path="config" element={guard(isAdmin, <ConfigPanel />)} />
						<Route path="*" element={<Navigate to={home} replace />} />
					</Route>
				</Routes>
			</Suspense>
		);
	}

	return (
		<BrowserRouter>
			<ErrorBanner />
			{content}
			<ToastHost />
		</BrowserRouter>
	);
}
